import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Tabs, Tab, Button, IconButton, Switch, Avatar,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Chip, Stack, List, ListItem, ListItemText, ToggleButton, ToggleButtonGroup,
   Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";


const API_BASE = process.env.REACT_APP_API_DOCTOR_APPOINTMENT_URL;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
});
const TEAL = "#006D77";
const DRAWER_WIDTH = 280;
const TABS = ["Doctors", "Time Slots", "Reasons for Visit"];

const AppointmentData = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#EEF4F5",
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        mt: { xs: "56px", md: 0 },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        <div className="back-button" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
        </div>
        <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom
          sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" } }}>
          Doctor Consultation — Admin
        </Typography>
        <Typography color="text.secondary" mb={3}>
          Manage doctors, their availability, and consult reasons shown to residents.
        </Typography>

        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: "divider", "& .Mui-selected": { color: TEAL } }}
            TabIndicatorProps={{ style: { backgroundColor: TEAL } }}>
            {TABS.map((label) => <Tab key={label} label={label} sx={{ textTransform: "none", fontWeight: 600 }} />)}
          </Tabs>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {tab === 0 && <DoctorManager />}
            {tab === 1 && <SlotManager />}
            {tab === 2 && <ReasonManager />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

// ============================================================
// Doctors
// ============================================================
const emptyDoctor = { name: "", qualification: "", specialization: "", reg_no: "", fee: "", location: "" };

const DoctorManager = () => {
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyDoctor);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API_BASE}/dashboard/doctors`, authHeaders()).then((r) => setDoctors(r.data || []));
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null); setForm(emptyDoctor);
    setPhotoFile(null); setPhotoPreview(""); setShowForm(true);
  };
  const openEdit = (doc) => {
    setEditingId(doc.id); setForm({ ...doc, fee: String(doc.fee ?? "") });
    setPhotoFile(null); setPhotoPreview(doc.photo_url || ""); setShowForm(true);
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, fee: parseFloat(form.fee) || 0 };
      let doctorId = editingId;
      if (editingId) {
        await axios.put(`${API_BASE}/dashboard/doctors/${editingId}`, payload, authHeaders());
      } else {
        const res = await axios.post(`${API_BASE}/dashboard/doctors`, payload, authHeaders());
        doctorId = res.data.id;
      }
      if (photoFile && doctorId) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        await axios.post(`${API_BASE}/dashboard/doctors/${doctorId}/photo`, fd, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (doc) => {
    await axios.put(`${API_BASE}/dashboard/doctors/${doc.id}`, { is_active: !doc.is_active }, authHeaders());
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this doctor and all their slots?")) return;
    await axios.delete(`${API_BASE}/dashboard/doctors/${id}`, authHeaders());
    load();
  };

  const field = (key, label, extra = {}) => (
    <TextField fullWidth margin="dense" label={label} value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...extra} />
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Doctors</Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: TEAL, textTransform: "none" }} onClick={openAdd}>
          Add Doctor
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Specialization</TableCell>
              <TableCell>Fee</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {doctors.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={d.photo_url} sx={{ bgcolor: TEAL }}>{d.name?.[0]}</Avatar>
                    <Box>
                      <Typography fontWeight={700}>{d.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{d.qualification}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{d.specialization}</TableCell>
                <TableCell>₹{d.fee}</TableCell>
                <TableCell><Switch checked={d.is_active} onChange={() => toggleActive(d)} /></TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(d)} sx={{ color: TEAL }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => remove(d.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {doctors.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>No doctors added yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editingId ? "Edit Doctor" : "Add Doctor"}</DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 2, mt: 1 }}>
            <Avatar src={photoPreview} sx={{ width: 96, height: 96, bgcolor: TEAL, fontSize: 34 }}>
              {form.name?.[0]}
            </Avatar>
            <Button component="label" startIcon={<PhotoCameraIcon />} size="small" sx={{ textTransform: "none", color: TEAL }}>
              Upload Photo
              <input hidden accept="image/*" type="file" onChange={onPickPhoto} />
            </Button>
          </Stack>
          {field("name", "Full name")}
          {field("qualification", "Qualification (e.g. MD Internal Medicine)")}
          {field("specialization", "Specialization")}
          {field("reg_no", "Registration No.")}
          {field("fee", "Consultation fee (₹)", { type: "number" })}
          {field("location", "Clinic / location")}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowForm(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={!form.name || saving} onClick={save} sx={{ bgcolor: TEAL, textTransform: "none" }}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};






// ============================================================
// Slots
// ============================================================
const SlotManager = () => {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]); // for adding
  const [range, setRange] = useState({ day_start: "17:00", day_end: "18:00", slot_minutes: 15 });
  const [saving, setSaving] = useState(false);
  const [viewDate, setViewDate] = useState(null); // the day whose slots are shown for editing

  useEffect(() => {
    axios.get(`${API_BASE}/dashboard/doctors`, authHeaders()).then((r) => {
      setDoctors(r.data || []);
      if (r.data.length) setDoctorId(r.data[0].id);
    });
  }, []);

  const loadSlots = (id = doctorId) => {
    if (!id) return;
    axios.get(`${API_BASE}/dashboard/doctors/${id}/slots`, authHeaders()).then((r) => setSlots(r.data || []));
  };
  useEffect(() => {
    if (doctorId) { loadSlots(doctorId); setViewDate(null); setSelectedDates([]); }
    // eslint-disable-next-line
  }, [doctorId]);

  // group slots by date
  const grouped = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const scheduledDates = Object.keys(grouped);

  const toggleDate = (dayjsDate) => {
    const iso = dayjsDate.format("YYYY-MM-DD");
    // if this date already has slots, open it for viewing/editing
    if (scheduledDates.includes(iso)) {
      setViewDate(iso);
      return;
    }
    // otherwise toggle it into the "add availability" selection
    setViewDate(null);
    setSelectedDates((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort()
    );
  };

  const addSlots = async () => {
    if (!doctorId || selectedDates.length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/dashboard/doctors/${doctorId}/slots/bulk-dates`, {
        dates: selectedDates, ...range,
      }, authHeaders());
      setSelectedDates([]);
      loadSlots();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (slot) => {
    await axios.put(`${API_BASE}/dashboard/slots/${slot.id}`, { is_active: !slot.is_active }, authHeaders());
    loadSlots();
  };
  const remove = async (slot) => {
    if (slot.is_booked) return;
    await axios.delete(`${API_BASE}/dashboard/slots/${slot.id}`, authHeaders());
    loadSlots();
  };

  // calendar day renderer: mark scheduled dates + selected-to-add dates
  const CustomDay = (props) => {
    const { day, ...rest } = props;
    const iso = day.format("YYYY-MM-DD");
    const isScheduled = scheduledDates.includes(iso);
    const isSelected = selectedDates.includes(iso);
    const isViewing = viewDate === iso;
    return (
      <PickersDay {...rest} day={day}
        sx={{
          ...(isScheduled && {
            bgcolor: "#E0F7F8", color: TEAL, fontWeight: 700,
            border: `1px solid ${TEAL}`, "&:hover": { bgcolor: "#B2E0E4" },
          }),
          ...(isSelected && { bgcolor: TEAL, color: "#fff", "&:hover": { bgcolor: "#005662" } }),
          ...(isViewing && { boxShadow: `0 0 0 2px ${TEAL}`, bgcolor: "#B2E0E4" }),
        }} />
    );
  };

  const viewSlots = viewDate ? (grouped[viewDate] || []) : [];

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>Time Slots</Typography>

      <TextField select label="Doctor" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
        sx={{ minWidth: { xs: "100%", sm: 240 }, mb: 3 }} size="small">
        {doctors.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
      </TextField>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        {/* ── Calendar ── */}
        <Box>
          <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: "#fff", mb: 1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar disablePast onChange={toggleDate} slots={{ day: CustomDay }} value={null} />
            </LocalizationProvider>
          </Paper>
          {/* Legend */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: "#E0F7F8", border: `1px solid ${TEAL}` }} />
              <Typography variant="caption" color="text.secondary">Has slots (click to edit)</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: TEAL }} />
              <Typography variant="caption" color="text.secondary">Selected to add</Typography>
            </Stack>
          </Stack>
        </Box>

        {/* ── Right panel: either "add" or "edit scheduled day" ── */}
        <Box flex={1} sx={{ width: "100%" }}>
          {viewDate ? (
            /* ---- Edit an existing scheduled day ---- */
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={700}>
                  {new Date(viewDate).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
                </Typography>
                <Button size="small" onClick={() => setViewDate(null)} sx={{ textTransform: "none", color: TEAL }}>
                  Close
                </Button>
              </Stack>

              <TableContainer>
                <Table size="small" sx={{ minWidth: 420 }}>
                  <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                    <TableRow>
                      <TableCell>Time</TableCell><TableCell>Status</TableCell>
                      <TableCell>Open</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewSlots.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.start_time} – {s.end_time}</TableCell>
                        <TableCell>
                          {s.is_booked
                            ? <Chip label="Booked" size="small" />
                            : s.is_active
                              ? <Chip label="Available" size="small" color="success" />
                              : <Chip label="Hidden" size="small" color="warning" />}
                        </TableCell>
                        <TableCell>
                          <Switch checked={s.is_active} disabled={s.is_booked} onChange={() => toggleActive(s)} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton color="error" disabled={s.is_booked} onClick={() => remove(s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {viewSlots.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center" sx={{ color: "text.secondary" }}>
                        No slots on this day.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            /* ---- Add availability to newly selected dates ---- */
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, bgcolor: "#F8FAFC" }}>
              <Typography fontWeight={700} mb={0.5}>Add availability</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Tap empty dates on the calendar to select them (multiple allowed), set a time range, then add.
              </Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={2}>
                <TextField label="From" type="time" size="small" value={range.day_start}
                  onChange={(e) => setRange({ ...range, day_start: e.target.value })} InputLabelProps={{ shrink: true }} />
                <TextField label="To" type="time" size="small" value={range.day_end}
                  onChange={(e) => setRange({ ...range, day_end: e.target.value })} InputLabelProps={{ shrink: true }} />
                <TextField select label="Slot length" size="small" value={range.slot_minutes}
                  onChange={(e) => setRange({ ...range, slot_minutes: e.target.value })} sx={{ minWidth: 120 }}>
                  {[10, 15, 20, 30].map((m) => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
                </TextField>
              </Stack>

              <Typography variant="body2" fontWeight={600} mb={1}>
                Selected dates ({selectedDates.length})
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                {selectedDates.length === 0 && <Typography variant="body2" color="text.secondary">None selected yet.</Typography>}
                {selectedDates.map((d) => (
                  <Chip key={d} label={dayjs(d).format("DD MMM")}
                    onDelete={() => setSelectedDates((prev) => prev.filter((x) => x !== d))}
                    sx={{ bgcolor: "#E0F7F8", color: TEAL, fontWeight: 600 }} />
                ))}
              </Stack>

              <Button variant="contained" disabled={selectedDates.length === 0 || saving}
                onClick={addSlots} sx={{ bgcolor: TEAL, textTransform: "none" }}>
                {saving ? "Adding..." : `Add slots to ${selectedDates.length} date(s)`}
              </Button>
            </Paper>
          )}
        </Box>
      </Stack>

      {slots.length === 0 && (
        <Typography color="text.secondary" mt={3}>No slots created for this doctor yet.</Typography>
      )}
    </Box>
  );
};








// ============================================================
// Reasons
// ============================================================
const ReasonManager = () => {
  const [reasons, setReasons] = useState([]);
  const [label, setLabel] = useState("");

  const load = () => axios.get(`${API_BASE}/dashboard/consult-reasons`, authHeaders()).then((r) => setReasons(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) return;
    await axios.post(`${API_BASE}/dashboard/consult-reasons`, { label: label.trim() }, authHeaders());
    setLabel("");
    load();
  };

  const toggleActive = async (r) => {
    await axios.put(`${API_BASE}/dashboard/consult-reasons/${r.id}`, { is_active: !r.is_active }, authHeaders());
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API_BASE}/dashboard/consult-reasons/${id}`, authHeaders());
    load();
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700}>Reasons for Visit</Typography>
      <Typography color="text.secondary" mb={2}>
        These appear as consult options for residents, e.g. Diabetes, Fever / Infection, Joint Pain.
      </Typography>

      <Stack direction="row" spacing={1.5} mb={3} sx={{ maxWidth: 480 }}>
        <TextField fullWidth size="small" placeholder="e.g. Thyroid" value={label}
          onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button variant="contained" onClick={add} sx={{ bgcolor: TEAL, textTransform: "none" }}>Add</Button>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, maxWidth: 600 }}>
        <List disablePadding>
          {reasons.map((r, i) => (
            <ListItem key={r.id} divider={i < reasons.length - 1}
              secondaryAction={
                <Stack direction="row" alignItems="center">
                  <Switch checked={r.is_active} onChange={() => toggleActive(r)} size="small" />
                  <IconButton color="error" onClick={() => remove(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              }>
              <ListItemText primary={r.label} />
            </ListItem>
          ))}
          {reasons.length === 0 && (
            <ListItem><ListItemText primary="No reasons added yet." primaryTypographyProps={{ color: "text.secondary" }} /></ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default AppointmentData;